import { z } from "zod";

/** Rep-confirmed campaign data. These values never grant execution capabilities. */
const text = z.string().trim().min(1).max(4_000);
const revision = z.uuid();
export const campaignActionSchema = z.enum([
  "acknowledge",
  "answer",
  "request_clarification",
  "share_resource",
  "qualify",
  "offer_meeting",
  "share_booking_link",
  "manage_booking",
  "share_registration_link",
  "collect_contribution",
  "collect_phone_call",
  "handoff",
]);
export type CampaignAction = z.infer<typeof campaignActionSchema>;

/*
 * The campaign goals. Exported as its own schema because more than one table is keyed by goal kind
 * and every one of them must be exhaustive over the same list: a new goal then fails the typecheck
 * wherever it has not been answered, rather than falling into a default that reads as "no goal".
 */
export const campaignGoalKindSchema = z.enum([
  "meeting",
  "event_participation",
  "commercial_exploration",
  "contribution_research",
  "resource_delivery",
  "phone_call",
]);
export type CampaignGoalKind = z.infer<typeof campaignGoalKindSchema>;

const campaignGoalObject = z.strictObject({
  configuration_revision: revision,
  kind: campaignGoalKindSchema,
  target: text,
  success_criterion: text,
  participant_role: z.enum(["attendee", "speaker", "commercial_partner"]).nullable(),
});
export const campaignGoalSchema = campaignGoalObject.refine(
  (goal) => (goal.kind === "event_participation") === (goal.participant_role !== null),
  {
    path: ["participant_role"],
    message: "Event participation requires a role; other goals must use null",
  },
);
export type CampaignGoal = z.infer<typeof campaignGoalSchema>;

/** An operator-maintained contact entry, scoped to the selected workspace. */
const handoffContactSchema = z.strictObject({
  workspace_id: z.number().int().positive(),
  contact_id: z.string().trim().min(1).max(256),
  name: z.string().trim().min(1).max(256),
  email: z.email(),
});
const nextStepPolicyObject = z.strictObject({
  configuration_revision: revision,
  permitted_actions: z
    .array(campaignActionSchema)
    .min(1)
    .refine((actions) => new Set(actions).size === actions.length, "Actions must be unique"),
  owner: z.enum(["agency", "client"]),
  handoff_contact: handoffContactSchema.nullable().exactOptional(),
});
export const nextStepPolicySchema = nextStepPolicyObject
  .refine(
    (policy) =>
      policy.permitted_actions.includes("acknowledge") &&
      policy.permitted_actions.includes("request_clarification"),
    {
      path: ["permitted_actions"],
      message:
        "Policies must permit acknowledge and request_clarification so deferral never forces pursuit",
    },
  )
  .refine((policy) => !policy.permitted_actions.includes("handoff") || !!policy.handoff_contact, {
    path: ["handoff_contact"],
    message: "A permitted handoff requires an explicitly confirmed workspace contact",
  })
  .refine((policy) => !policy.handoff_contact || policy.permitted_actions.includes("handoff"), {
    path: ["handoff_contact"],
    message: "A handoff contact requires a permitted handoff action",
  });
export type NextStepPolicy = z.infer<typeof nextStepPolicySchema>;

// Keep refinements on the complete values: the write operation injects the revision before parsing.
export const campaignConfigurationInputSchema = z.strictObject({
  goal: campaignGoalObject.omit({ configuration_revision: true }),
  policy: nextStepPolicyObject.omit({ configuration_revision: true }),
});
export const configureCampaignInputSchema = campaignConfigurationInputSchema.extend({
  workspaceId: z.union([z.string().trim().min(1).max(256), z.number().int()]).transform(String),
  campaignId: z.union([z.string().trim().min(1).max(256), z.number().int()]).transform(String),
  operationId: revision,
  expectedRevision: revision.nullable(),
});
export type ConfigureCampaignInput = z.input<typeof configureCampaignInputSchema>;

export function isCampaignConfigurationKey(key: string): boolean {
  return key === "campaign_goal" || key === "next_step_policy";
}
